import { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';

// Brilho dourado pulsante (respiração lenta) + divisor sutil — refino elegante.
const PROMO_STYLES = `
@keyframes promo-glow { 0%,100%{opacity:.55;transform:scale(1)} 50%{opacity:1;transform:scale(1.35)} }
@keyframes promo-halo { 0%{opacity:.5;transform:scale(.8)} 70%,100%{opacity:0;transform:scale(2.6)} }
`;

const STORAGE_KEY = 'free_shipping_promo_end_v2';
// 1h 27min = 87 minutos — tempo "quebrado" gera mais credibilidade que números redondos
const PROMO_DURATION_MS = (1 * 60 + 27) * 60 * 1000;

/**
 * Retorna o timestamp final do temporizador.
 * - Usa sessionStorage (NÃO localStorage): assim cada NOVO visitante recebe um timer fresco,
 *   mas se a MESMA pessoa atualizar a página dentro da mesma sessão, o tempo continua de onde parou.
 * - sessionStorage é limpo automaticamente quando a aba/navegador é fechado → novo visitante = novo timer.
 */
function getOrCreatePromoEnd() {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      const ts = parseInt(existing, 10);
      if (!Number.isNaN(ts) && ts > Date.now()) {
        return ts;
      }
    }
    const newEnd = Date.now() + PROMO_DURATION_MS;
    sessionStorage.setItem(STORAGE_KEY, String(newEnd));
    return newEnd;
  } catch {
    return Date.now() + PROMO_DURATION_MS;
  }
}

function formatRemaining(ms) {
  if (ms <= 0) return { h: '00', m: '00', s: '00', expired: true };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return {
    h: String(h).padStart(2, '0'),
    m: String(m).padStart(2, '0'),
    s: String(s).padStart(2, '0'),
    expired: false,
  };
}

/**
 * Banner promocional: "Frete grátis em pedidos com 2+ peças" + countdown 24h.
 * Mostra em HomePage, ProductsPage e ProductDetailPage.
 * Cronômetro persiste por sessão do usuário (24h por visitante) — gera urgência.
 *
 * Props:
 *  - variant: 'full' (banner com gradiente, padrão) | 'compact' (linha mais discreta)
 */
export default function FreeShippingPromoBanner({ variant = 'full' }) {
  const [endTs, setEndTs] = useState(null);
  const [remaining, setRemaining] = useState({ h: '01', m: '27', s: '00', expired: false });

  useEffect(() => {
    // Cada visitante NOVO recebe um timer fresco (sessionStorage).
    // Se o mesmo visitante atualizar a página, o tempo continua de onde parou (não reinicia).
    setEndTs(getOrCreatePromoEnd());
  }, []);

  useEffect(() => {
    if (!endTs) return;
    const tick = () => {
      const rem = formatRemaining(endTs - Date.now());
      setRemaining(rem);
      if (rem.expired) {
        // Se expirou enquanto o cliente ainda está no site, renova silenciosamente
        // para manter o gatilho de urgência ativo (caso ele permaneça >1h27 navegando)
        try {
          const newEnd = Date.now() + PROMO_DURATION_MS;
          sessionStorage.setItem(STORAGE_KEY, String(newEnd));
          setEndTs(newEnd);
        } catch {}
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endTs]);

  // Banner não pode ser fechado — gatilho de urgência constante (CRO)

  if (!endTs) return null;

  if (variant === 'compact') {
    return (
      <div className="w-full bg-gradient-to-r from-[#0d0d0d] via-[#161616] to-[#0d0d0d] text-white text-center py-2 px-3 text-xs flex items-center justify-center gap-2.5 uppercase tracking-[0.14em] border-b border-[#C9A961]/30" data-testid="promo-banner-compact">
        <style>{PROMO_STYLES}</style>
        <Truck className="h-3.5 w-3.5 text-[#C9A961]" />
        <span className="font-semibold text-[#C9A961]">Frete grátis em 2+ peças</span>
        <span className="h-3 w-px bg-gradient-to-b from-transparent via-[#C9A961]/45 to-transparent" aria-hidden="true" />
        <span className="relative inline-flex h-1.5 w-1.5 items-center justify-center" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#C9A961]" style={{ animation: 'promo-halo 2.6s ease-out infinite' }} />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#C9A961]" style={{ animation: 'promo-glow 2.6s ease-in-out infinite' }} />
        </span>
        <span className="font-light text-white/80">Promoção por tempo limitado</span>
      </div>
    );
  }

  return (
    <div
      className="relative w-full overflow-hidden bg-gradient-to-r from-[#0d0d0d] via-[#161616] to-[#0d0d0d] text-white border-b border-[#C9A961]/30"
      data-testid="promo-banner-full"
    >
      <style>{PROMO_STYLES}</style>
      {/* Shimmer dourado suave (5s) */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '50%',
            height: '100%',
            background:
              'linear-gradient(90deg, transparent 0%, rgba(201,169,97,0.06) 30%, rgba(201,169,97,0.18) 50%, rgba(201,169,97,0.06) 70%, transparent 100%)',
            animation: 'shimmer-slide 5s ease-in-out infinite',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-2.5 sm:py-3 flex items-center justify-center gap-x-2.5 gap-y-1.5 sm:gap-6 flex-wrap text-center">
        {/* Mensagem-herói: FRETE GRÁTIS em destaque */}
        <div className="flex items-center gap-2 sm:gap-3.5">
          <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-[#C9A961]/12 border border-[#C9A961]/30 flex items-center justify-center shrink-0">
            <Truck className="h-3.5 w-3.5 sm:h-[18px] sm:w-[18px] text-[#C9A961]" />
          </div>
          <p className="leading-snug text-left" style={{ fontFamily: 'var(--font-body)' }}>
            <span className="text-[12px] sm:text-[15px] font-bold uppercase text-[#C9A961] tracking-[0.1em] sm:tracking-[0.16em]">Frete grátis</span>
            <span className="hidden sm:inline text-white/55 text-sm font-light"> para todo o Brasil</span>
            <span className="inline text-white/70 text-[11px] sm:text-sm font-light"> em pedidos com </span>
            <span className="text-white text-[11px] sm:text-sm font-semibold">2 peças ou mais</span>
          </p>
        </div>

        {/* Divisor dourado fino */}
        <span className="hidden sm:block h-6 w-px bg-gradient-to-b from-transparent via-[#C9A961]/45 to-transparent" aria-hidden="true" />

        {/* Selo "tempo limitado" com brilho dourado pulsante */}
        <div
          className="flex items-center gap-1.5 sm:gap-2.5 rounded-full border border-[#C9A961]/25 bg-[#C9A961]/[0.06] px-2.5 py-1 sm:px-3.5 sm:py-1.5 backdrop-blur-sm"
          style={{ boxShadow: '0 0 18px -6px rgba(201,169,97,0.45)' }}
          data-testid="promo-banner-limited-badge"
        >
          <span className="relative inline-flex h-1.5 w-1.5 items-center justify-center" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#C9A961]" style={{ animation: 'promo-halo 2.6s ease-out infinite' }} />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#C9A961]" style={{ animation: 'promo-glow 2.6s ease-in-out infinite' }} />
          </span>
          <span className="text-[9px] sm:text-[11px] uppercase tracking-[0.1em] sm:tracking-[0.22em] font-medium text-[#C9A961]/95 whitespace-nowrap">Promoção por tempo limitado</span>
        </div>
      </div>
    </div>
  );
}
